import React, { useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraSelect } from "../../../components/AtiraSelect";
import { Flex } from "../../../components/Flex";
import { AppLangs } from "../../../model/shared/enum/AppLangs.enum";
import { UpdateUserSettingsDto } from "../../../model/user/settings/dto/UpdateUserSettingsDto";
import { Spacing } from "../../../theme/Spacing";
import { Button } from "../../../components/Button";
import { AtiraToast } from "../../../utils/AtiraToast";
import { Info } from "../../../components/Info";
import { SettingsWrapper } from "../components/SettingsWrapper";
import { TestIDs } from "../../../tests/types/testIds";

const SaveButton = styled(Button)`
  margin: 0;
  width: fit-content;
  margin-top: ${Spacing.m};
`;

type SettingsLanguageProps = {
  onSave: VoidFunction;
};

export const SettingsLanguage: React.FC<SettingsLanguageProps> = ({
  onSave,
}) => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const { control } = useFormContext<UpdateUserSettingsDto>();

  const langOptions = useMemo(() => {
    return Object.values(AppLangs).map((lang) => ({
      label: t(`settings.language.${lang.toLowerCase()}`),
      value: lang,
      "data-testid": TestIDs.general.settings.language.option(lang),
    }));
  }, [t]);

  const localeOnSave = async () => {
    try {
      setLoading(true);
      await onSave();
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SettingsWrapper>
      <Flex flexDirection="column" gap="s">
        <Controller
          name="lang"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <AtiraSelect
              options={langOptions}
              value={langOptions.find((option) => option.value === value)}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
              title={t("settings.language.lang.title")}
              className={TestIDs.general.settings.language.open}
            />
          )}
        />

        <Info info={t("settings.language.lang.description")} />
        <Info info={t("settings.language.reload-note")} />
      </Flex>

      <SaveButton
        onClick={localeOnSave}
        loading={loading}
        dataTestId={TestIDs.general.settings.language.button}
      >
        {t("common.save_changes")}
      </SaveButton>
    </SettingsWrapper>
  );
};
